import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import api from "./api";
import {handleResponseStatus, updateIdField, updateItem} from "./utils";

const initialState = {
    items: [],
    fetchStatus: "success",
    fetchByIdStatus: "idle",
    createStatus: "idle",
    updateStatus: "idle",
    deleteStatus: "idle",
    pagination: {},
    error: null
};

export const fetchUsers = createAsyncThunk('user/fetchUsers', async ({page = 1, limit = 10} = {}) => {
    try {
        const response = await api.get(`/sdg/user?page=${page}&limit=${limit}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const createUser = createAsyncThunk('user/createUser', async (data) => {
    try {
        const response = await api.post(`/sdg/user`, data, {
            useMultipart: true,
        });
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const deleteUser = createAsyncThunk('user/deleteUser', async (id) => {
    try {
        const response = await api.delete(`/sdg/user/${id}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const updateUser = createAsyncThunk('user/updateUser', async (data) => {
    try {
        const response = await api.patch(`/sdg/user/${data.id}`, data, {
            useMultipart: true,
        });
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const fetchUserById = createAsyncThunk('user/fetchUserById', async (id) => {
    try {
        const response = await api.get(`/sdg/user/${id}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

const userSlice = createSlice({
    name: "user",
    initialState,
    reducers: {
        resetUpdateStatus(state) {
            state.updateStatus = 'idle';
        },
        resetDeleteStatus(state) {
            state.deleteStatus = 'idle';
        },
        resetCreateStatus(state) {
            state.createStatus = 'idle';
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUsers.pending, (state) => {
                state.fetchStatus = 'loading';
            })
            .addCase(fetchUsers.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, 'fetchStatus');
                const {data, ...pagination} = action.payload.data;
                state.items = fetchStatus === "success" ? updateIdField(data) : state.items;
                state.fetchStatus = fetchStatus;
                state.pagination = pagination;
                state.error = error;
            })
            .addCase(fetchUsers.rejected, (state, action) => {
                state.fetchStatus = 'failed';
                state.error = action.error.message;
            })

            .addCase(fetchUserById.pending, (state) => {
                state.fetchByIdStatus = 'loading';
            })
            .addCase(fetchUserById.fulfilled, (state, action) => {
                const {fetchByIdStatus, error} = handleResponseStatus(action.payload, 'fetchByIdStatus');
                state.items = fetchByIdStatus === "success" ? updateItem(action.payload.data.data, state.items) : state.items;
                state.fetchByIdStatus = fetchByIdStatus;
                state.error = error;
            })
            .addCase(fetchUserById.rejected, (state, action) => {
                state.fetchByIdStatus = 'failed';
                state.error = action.error.message;
            })
            .addCase(createUser.pending, (state) => {
                state.createStatus = 'loading';
            })
            .addCase(createUser.fulfilled, (state, action) => {
                const {createStatus, error} = handleResponseStatus(action.payload, 'createStatus');
                state.createStatus = createStatus;
                state.error = error;
            })
            .addCase(createUser.rejected, (state, action) => {
                state.createStatus = 'failed';
                state.error = action.error.message;
            })
            .addCase(updateUser.pending, (state) => {
                state.updateStatus = 'loading';
            })
            .addCase(updateUser.fulfilled, (state, action) => {
                const {updateStatus, error} = handleResponseStatus(action.payload, 'updateStatus');
                state.updateStatus = updateStatus;
                state.error = error;
            })
            .addCase(updateUser.rejected, (state, action) => {
                state.updateStatus = 'failed';
                state.error = action.error.message;
            })
            .addCase(deleteUser.pending, (state) => {
                state.deleteStatus = 'loading';
            })
            .addCase(deleteUser.fulfilled, (state, action) => {
                const {deleteStatus, error} = handleResponseStatus(action.payload, 'deleteStatus');
                state.deleteStatus = deleteStatus;
                state.items = state.items.filter((item) => item.id !== action.payload.data.data._id);
                state.error = error;
            })
            .addCase(deleteUser.rejected, (state, action) => {
                state.deleteStatus = 'failed';
                state.error = action.error.message;
            });
    }
});

export const {resetUpdateStatus, resetDeleteStatus, resetCreateStatus} = userSlice.actions;
export default userSlice.reducer;
